import React, { useState, useEffect } from 'react';
import { placementDriveService, coordinatorService, countryService, stateService, districtService, collegeService } from '../../services/api';
import './AdminPage.css';

function AdminPlacementDrives() {
  const [placementDrives, setPlacementDrives] = useState([]);
  const [coordinators, setCoordinators] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlacementDrive, setEditingPlacementDrive] = useState(null);
  const [formData, setFormData] = useState({
    Title: '',
    Description: '',
    LastDate: '',
    CompanyId: '',
    CordinatorId: '',
    CountryId: '',
    StateId: '',
    DistrictId: '',
    CollegeId: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
    loadDropdowns();
  }, []);

  useEffect(() => {
    if (formData.CountryId) {
      loadStates();
    }
  }, [formData.CountryId]);

  useEffect(() => {
    if (formData.StateId) {
      loadDistricts();
    }
  }, [formData.StateId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await placementDriveService.getPlacementDrives();
      setPlacementDrives(data);
    } catch (error) {
      alert('Error loading placement drives: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDropdowns = async () => {
    try {
      const [coordinatorsData, countriesData, collegesData] = await Promise.all([
        coordinatorService.getCoordinators(),
        countryService.getCountries(),
        collegeService.getColleges()
      ]);
      setCoordinators(coordinatorsData);
      setCountries(countriesData);
      setColleges(collegesData);
    } catch (error) {
      console.error('Error loading dropdowns:', error);
    }
  };

  const loadStates = async () => {
    try {
      const data = await stateService.getStates();
      setStates(data.filter(s => s.CountryId === parseInt(formData.CountryId)));
    } catch (error) {
      console.error('Error loading states:', error);
    }
  };

  const loadDistricts = async () => {
    try {
      const data = await districtService.getDistricts();
      setDistricts(data.filter(d => d.StateId === parseInt(formData.StateId)));
    } catch (error) {
      console.error('Error loading districts:', error);
    }
  };

  const handleAdd = () => {
    setEditingPlacementDrive(null);
    setFormData({
      Title: '',
      Description: '',
      LastDate: '',
      CompanyId: '',
      CordinatorId: '',
      CountryId: '',
      StateId: '',
      DistrictId: '',
      CollegeId: ''
    });
    setIsModalOpen(true);
  };

  const handleEdit = (placementDrive) => {
    setEditingPlacementDrive(placementDrive);
    setFormData({
      Id: placementDrive.Id,
      Title: placementDrive.Title || '',
      Description: placementDrive.Description || '',
      LastDate: placementDrive.LastDate ? placementDrive.LastDate.split('T')[0] : '',
      CompanyId: placementDrive.CompanyId || '',
      CordinatorId: placementDrive.CordinatorId || '',
      CountryId: placementDrive.CountryId || '',
      StateId: placementDrive.StateId || '',
      DistrictId: placementDrive.DistrictId || '',
      CollegeId: placementDrive.CollegeId || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this placement drive?')) {
      try {
        await placementDriveService.deletePlacementDrive(id);
        loadData();
      } catch (error) {
        alert('Error deleting placement drive: ' + error.message);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = {
        ...formData,
        CompanyId: formData.CompanyId || null
      };
      await placementDriveService.savePlacementDrive(payload);
      setIsModalOpen(false);
      loadData();
    } catch (error) {
      alert('Error saving placement drive: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="page-header page-headeradmin">
        <h1>Placement Drives Management</h1>
        <button className="btn-primary" onClick={handleAdd}>Add Placement Drive</button>
      </div>

      {loading && !placementDrives.length ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Company</th>
                <th>College</th>
                <th>Last Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {placementDrives.map((drive) => (
                <tr key={drive.Id}>
                  <td>{drive.Title}</td>
                  <td>{drive.Company?.Name}</td>
                  <td>{drive.College?.Name}</td>
                  <td>{drive.LastDate ? new Date(drive.LastDate).toLocaleDateString() : ''}</td>
                  <td>
                    <button className="btn-edit" onClick={() => handleEdit(drive)}>Edit</button>
                    <button className="btn-delete" onClick={() => handleDelete(drive.Id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingPlacementDrive ? 'Edit Placement Drive' : 'Add Placement Drive'}</h2>
              <button className="modal-close" onClick={() => setIsModalOpen(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={formData.Title}
                    onChange={(e) => setFormData({ ...formData, Title: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group full-width">
                  <label>Description</label>
                  <textarea
                    value={formData.Description}
                    onChange={(e) => setFormData({ ...formData, Description: e.target.value })}
                    rows="3"
                  />
                </div>
                <div className="form-group">
                  <label>Last Date</label>
                  <input
                    type="date"
                    value={formData.LastDate}
                    onChange={(e) => setFormData({ ...formData, LastDate: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Coordinator</label>
                  <select
                    value={formData.CordinatorId}
                    onChange={(e) => setFormData({ ...formData, CordinatorId: e.target.value })}
                  >
                    <option value="">Select Coordinator</option>
                    {coordinators.map((coordinator) => (
                      <option key={coordinator.Id} value={coordinator.Id}>{coordinator.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <select
                    value={formData.CountryId}
                    onChange={(e) => setFormData({ ...formData, CountryId: e.target.value, StateId: '', DistrictId: '' })}
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option key={country.Id} value={country.Id}>{country.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>State</label>
                  <select
                    value={formData.StateId}
                    onChange={(e) => setFormData({ ...formData, StateId: e.target.value, DistrictId: '' })}
                    disabled={!formData.CountryId}
                  >
                    <option value="">Select State</option>
                    {states.map((state) => (
                      <option key={state.Id} value={state.Id}>{state.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>District</label>
                  <select
                    value={formData.DistrictId}
                    onChange={(e) => setFormData({ ...formData, DistrictId: e.target.value })}
                    disabled={!formData.StateId}
                  >
                    <option value="">Select District</option>
                    {districts.map((district) => (
                      <option key={district.Id} value={district.Id}>{district.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>College</label>
                  <select
                    value={formData.CollegeId}
                    onChange={(e) => setFormData({ ...formData, CollegeId: e.target.value })}
                  >
                    <option value="">Select College</option>
                    {colleges.map((college) => (
                      <option key={college.Id} value={college.Id}>{college.Name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPlacementDrives;

